import {
  CampaignOutlined,
  GroupsOutlined,
  MonetizationOnOutlined,
} from '@mui/icons-material';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import {
  Box,
  CircularProgress,
  Container,
  CssBaseline,
  Grid,
  Typography,
} from '@mui/material';
import { styled, StyledEngineProvider, ThemeProvider, useTheme } from '@mui/material/styles';
import axios from 'axios';
import dynamic from 'next/dynamic';
import { lazy, Suspense } from 'react';
import BlankLayout from 'src/@core/layouts/BlankLayout';
import { formatCurrency } from 'src/@core/utils/format';
import apiConfig from 'src/configs/auth';
import Logger from 'src/utils/logger';

// Lazy load heavy components
const CampaignHeader = dynamic(
  () => import('src/views/components/CampaignHeader/CampaignHeader')
);
const Hero = dynamic(() => import('src/views/components/Hero/Hero'));
const Benefits = dynamic(
  () => import('src/views/components/Benefits/Benefits')
);

const GradientBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(12, 0),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.05)',
    backdropFilter: 'blur(5px)',
  },
  '& h2': {
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(6),
    position: 'relative',
    fontWeight: 700,
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: -16,
      left: '50%',
      transform: 'translateX(-50%)',
      width: 80,
      height: 4,
      background: theme.palette.secondary.main,
      borderRadius: 2,
    },
  },
  '& p': {
    maxWidth: '800px',
    margin: '0 auto',
    marginBottom: theme.spacing(3),
    lineHeight: 1.8,
    color: theme.palette.text.primary,
    fontSize: '1.1rem',
    fontWeight: 500,
  },
}));

const ColorfulFooter = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
  color: theme.palette.common.white,
  padding: theme.spacing(4, 0),
}));

// Lazy load ContactUs component
const ContactUsSection = lazy(() => import('./ContactUsSection'));

const AboutUs = () => (
  <GradientBox>
    <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
      <Typography
        align="center"
        component="h2"
        gutterBottom
        sx={{
          fontSize: { xs: '2.5rem', md: '3.5rem' },
          textShadow: 'none',
          mb: 6,
        }}
        variant="h2"
      >
        About Us
      </Typography>
      <Typography
        sx={{
          textAlign: 'center',
          opacity: 0.95,
          mb: 4,
          px: { xs: 2, md: 8 },
        }}
        variant="h6">
        Welcome to GivingAmp, where we amplify the power of generosity! Our
        mission is to empower individuals and organizations to make a meaningful
        impact. We connect visionary campaigners with generous donors, fostering
        a community dedicated to positive change.
      </Typography>
      <Typography
        sx={{
          textAlign: 'center',
          opacity: 0.95,
          px: { xs: 2, md: 8 },
          marginBottom: "16px"
        }}
        variant="h6">
        At GivingAmp, we believe that every act of giving, no matter how small,
        can create ripples of change. Our platform offers user-friendly tools
        and innovative features to amplify your impact, making it easier than
        ever to launch campaigns, engage supporters, and track progress. Join us
        in turning compassion into action and building a better world, one
        campaign at a time.
      </Typography>
    </Container>
  </GradientBox>
);

const ContactUs = () => {
  return (
    <Suspense
      fallback={
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      }
    >
      <ContactUsSection />
    </Suspense>
  );
};

export const Footer = () => {
  return (
    <ColorfulFooter>
      <Container maxWidth="lg">
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid size={{ xs: 12, md: 4 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: { xs: 'center', md: 'flex-start' },
              }}
            >
              <Typography
                component="h6"
                fontWeight="bold"
                sx={{ display: 'flex', alignItems: 'center', mr: 2 }}
                variant="subtitle1"
              >
                <VolunteerActivismIcon sx={{ mr: 0.5, fontSize: '1.2rem' }} />
                GivingAmp
              </Typography>
            </Box>
          </Grid>

          <Grid
            size={{ xs: 12, md: 4 }}
            sx={{ textAlign: 'center', my: { xs: 1, md: 0 } }}
          >
            <Typography variant="caption">
              © {new Date().getFullYear()} GivingAmp. All rights reserved.
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'center', md: 'flex-end' },
              }}
            >
              <Typography fontWeight="bold" variant="subtitle2">
                Contact Us
              </Typography>
              <Typography variant="caption">
                Email: info@GivingAmp.com | Phone: +1 (646) 598-4770
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </ColorfulFooter>
  );
};

const StatsBox = styled(Box)(({ theme }) => ({
  background: theme.palette.background.paper,
  padding: theme.spacing(8, 0),
  textAlign: 'center',
}));

const ImpactStats = ({ platformStats }) => {
  // Use the stats passed from getStaticProps directly
  const stats = {
    donor: platformStats?.total_donors || 0,
    raised: platformStats?.total_raised || 0,
    total_campaigns: platformStats?.total_campaigns || 0,
  };

  return (
    <StatsBox>
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {[
            {
              number: (stats.donor || 0).toLocaleString(),
              label: 'Donors',
              icon: <GroupsOutlined />,
            },
            {
              number: formatCurrency(stats.raised || 0),
              label: 'Raised',
              icon: <MonetizationOnOutlined />,
            },
            {
              number: stats.total_campaigns.toString(),
              label: 'Campaigns',
              icon: <CampaignOutlined />,
            },
          ].map((stat, index) => (
            <Grid key={index} size={{ xs: 12, sm: 6, md: 4 }}>
              <Box sx={{ mb: 2 }}>{stat.icon}</Box>
              <Typography component="div" gutterBottom variant="h3">
                {stat.number}
              </Typography>
              <Typography variant="h6">{stat.label}</Typography>
            </Grid>
          ))}
        </Grid>
      </Container>
    </StatsBox>
  );
};

const HowItWorks = () => (
  <Box sx={{ py: 8, bgcolor: 'background.paper' }}>
    <Container maxWidth="lg">
      <Typography align="center" gutterBottom variant="h2">
        How It Works
      </Typography>
      <Grid container spacing={4} sx={{ mt: 4 }}>
        {[
          {
            title: 'Create Campaign',
            desc: 'Set up your fundraising campaign in minutes',
          },
          {
            title: 'Share',
            desc: 'Share your campaign with friends and family',
          },
          {
            title: 'Collect Donations',
            desc: 'Receive donations and track progress',
          },
          { title: 'Make Impact', desc: 'Use funds to make a difference' },
        ].map((step, index) => (
          <Grid key={index} size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography color="primary" sx={{ mb: 2 }} variant="h1">
                {index + 1}
              </Typography>
              <Typography gutterBottom variant="h6">
                {step.title}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {step.desc}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Container>
  </Box>
);

const Home = ({ platformStats = {} }) => {
  const theme = useTheme();

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
          <main>
            <CampaignHeader />
            <Hero />
            <ImpactStats platformStats={platformStats} />
            <Benefits />
            <HowItWorks />
            <AboutUs />
            <ContactUs />
          </main>
          <Footer />
        </Box>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

Home.getLayout = (page) => <BlankLayout>{page}</BlankLayout>;
Home.guestGuard = false;
Home.authGuard = false;

export async function getStaticProps() {
  try {
    // Fetch platform statistics at build time
    let platformStats = {};

    try {
      const statsResponse = await axios.get(
        `${apiConfig.BASE_URL}/api/home-hero-campaign-statistics`
      );
      const statsData = statsResponse.data;
      platformStats = {
        total_campaigns: statsData.total_campaigns || 0,
        total_raised: statsData.raised || 0,
        total_donors: statsData.donor || 0,
        active_campaigns: 0,
      };
    } catch (error) {
      Logger.error('Error fetching stats:', error);
    }

    return {
      props: {
        platformStats,
      },
      // Re-generate the page every 24 hours
      revalidate: 86_400, // 24 hours in seconds
    };
  } catch (error) {
    Logger.error('Error in getStaticProps:', error);
    return {
      props: {
        platformStats: {},
        error: 'Failed to load data',
      },
      revalidate: 3600, // Try again in 1 hour if failed
    };
  }
}

export default Home;
